
/**
 * Apply all properties from a secondary object onto the primary object, recursively
 * - use for taking a default/current object and apply new values, even if only a few is set
 * - can also be used for simply copying an object
 * Limitations: arrays will be copied, not merged!
 * @param primary The object to apply the secondary values to
 * @param secondary The object which values will be added/applied to the primary object
 * @returns The primary object
 */
export function extendObject<T>(primary: object, secondary: object) : T {
	for (const key in secondary) {
		if (secondary.hasOwnProperty(key)) {
			// If it's an object
			if (Object.prototype.toString.call(secondary[key as keyof object]) === '[object Object]') {
				if (!primary[key as keyof object]) { (primary[key as keyof object] as object) = {} }
				extendObject(primary[key as keyof object], secondary[key as keyof object])
			}

			// If it's an array - an array will always overwrite, not append!
			else if (Object.prototype.toString.call(secondary[key as keyof object]) == '[object Array]') {
				(primary[key as keyof object] as unknown[]) = [];

				(secondary[key as keyof object] as unknown[]).forEach((item: unknown) => {
					if (typeof item === 'object' && item !== null) {
						// Do object copying
						const obj = {};

						(primary[key as keyof object] as unknown[]).push(obj)
						extendObject(obj, item)
					} else {
						// Do primitive copying
						(primary[key as keyof object] as unknown[]).push(item)
					}
				})

				// It's a primitive
			} else {
				primary[key as keyof object] = secondary[key as keyof object]
			}
		}
	}
	return primary as T
}

export function safeForClone<T extends Record<string, any>>(obj: T): Partial<T> { // eslint-disable-line
	const result: Partial<T> = {}

	for (const [key, value] of Object.entries(obj) as [keyof T, any][]) { // eslint-disable-line
		if (typeof value !== 'function') {
			result[key] = value
		}
	}

	return result
}

/**
 * Traverses an object and removes properties than are not in the array of permitted properties.
 * Limitations: only works on level 0
 * @param obj The object to check
 * @param properties Array of valid property names
 * @param reverse If true the properties list instead contains invalid property names
 * @returns The pruned object
 */
export function pruneObject<T>(obj: T, properties: string[], reverse?: boolean) {
	for (const key in obj) {
		if (reverse) {
			if ((obj as object).hasOwnProperty(key) && properties.includes(key)) {
				delete obj[key as keyof object] // eslint-disable-line
			}
		} else if ((obj as object).hasOwnProperty(key) && !properties.includes(key)) {
			delete obj[key as keyof T] // eslint-disable-line
		}
	}
	return obj
}

/**
 * Simple object cloning, only properties will be passed over
 * @param obj Object to clone
 * @returns A clone of the object
 */
export function cloneObject<T>(obj: T) : T {
	return JSON.parse(JSON.stringify(obj))
}

/**
 * Get the boolean value of a string or boolean
 * @param value The value to check - true, True, TRUE, 1, yes, YES, Yes, Y, y is considered true
 * @return The boolean value
 */
export function bool(value: boolean | string | number) {
	if (!value) { return false }
	if (typeof value === 'string') {
		return ['true', 'yes', '1', 'y'].includes(value.toLowerCase())
	} else if (typeof value === 'boolean') {
		return value
	} else if (typeof value === 'number') {
		return value > 0
	} else {
		return false // not a string and not a boolean
	}
}

export enum DK_NUMBER_TYPE {
	MOBILE = 'MOBILE',
	FIXED = 'FIXED',
	RESERVED = 'RESERVED',
	SPECIAL = 'SPECIAL',
	M2M = 'M2M',
	INVALID = 'INVALID'
}

/**
 * Returns the type of a Danish number based on Erhvervsstyrelsen number plan
 * - based on https://sdfi.dk/digital-infrastruktur/telefoni/numre/den-samlede-danske-nummerplan
 * @param number The phone number to check
 * @param internationalIsMobile If true, the number is considered mobile if it does not start with +45
 * @return MOBILE, FIXED, RESERVED, SPECIAL
 */
export function dkNumberType(number: string, internationalIsMobile = false) {
	if (internationalIsMobile && number && number.length > 10 && !number.startsWith('+45')) { return DK_NUMBER_TYPE.MOBILE }
	if (number.startsWith('+45')) { number = number.substring(3) }
	if (number.length == 12 && number.startsWith('37')) { return DK_NUMBER_TYPE.M2M }
	if (number.length != 8) { return DK_NUMBER_TYPE.INVALID }

	// Test order matters!!!
	const mobile = /^(2|3[01]|4[0-2]|5[0-3]|6[01]|71|81|9[1-3]|34[24-9]|35[6-79]|36[256]|389|398|431|441|462|466|468|47[2468]|48[5689]|49[3-689]|54[235]|55[126]|57[1-479]|58[4679]|59[78]|62[79]|64[19]|658|66[2-57]|69[2-47]|77[12]|78[235689]|82[679])/
	const reserved = /^(0[1-9]|1[3-5]|17|19|67|68|83|84|85|94|95)/
	const special = /^(00|11|12|16|18|37|80|90)/
	const fixed = /^(3[2-689]|4[3-9]|5[4-9]|6[2-69]|7[02-9]|8[26-9]|9[6-9])/

	if (mobile.test(number)) { return DK_NUMBER_TYPE.MOBILE }
	if (reserved.test(number)) { return DK_NUMBER_TYPE.RESERVED }
	if (special.test(number)) { return DK_NUMBER_TYPE.SPECIAL }
	if (fixed.test(number)) { return DK_NUMBER_TYPE.FIXED }

	return DK_NUMBER_TYPE.INVALID
}

/**
 * Array.sort function for arrays of objects. Can sort on any property in the object.
 * @example People.sort(DynamicSort("Name"));
 * @param property The property to sort by, prefix with minus to reverse sort, prefix with # for case insensitive
 * @returns Sort order
 */
export function dynamicSort(property: string) {
	let sortOrder = 1, caseInsensitive = false
	if (property.startsWith('-')) {
		sortOrder = -1
		property = property.substring(1)
	} else if (property.startsWith('#')) {
		caseInsensitive = true
		property = property.substring(1)
	}

	const property2 = property as keyof object

	return function(a: object, b: object) {
		let result

		if ((a[property2] as Date) instanceof Date && (b[property2] as Date) instanceof Date) {
			const part1 = (a[property2] as Date).getTime() < (b[property2] as Date).getTime()
			const part2 = (a[property2] as Date).getTime() > (b[property2] as Date).getTime() ? 1 : 0

			result = part1 ? -1 : part2
		} else if (caseInsensitive) {
			const part1 = (a[property2] as string).toLowerCase() < (b[property2] as string).toLowerCase()
			const part2 = (a[property2] as string).toLowerCase() > (b[property2] as string).toLowerCase() ? 1 : 0

			result = part1 ? -1 : part2
		} else {
			const part1 = a[property2] < b[property2]
			const part2 = a[property2] > b[property2] ? 1 : 0

			result = part1 ? -1 : part2
		}
		return result * sortOrder
	}
}

/**
 * Array.sort function for arrays of objects. Can sort on multiple properties in the object.
 * @example People.sort(DynamicSortMultiple("Name", "-Surname"));
 * @params arguments One or more properties to sort by, prefix with minus to reverse sort
 * @returns Sort order
 */
export function dynamicSortMultiple(...props: string[]) {
	const ds = dynamicSort

	return function(obj1: object, obj2: object) {
		let index = 0, result = 0
		const numberOfProperties = props.length

		while (result === 0 && index < numberOfProperties) {
			result = ds(props[index] as string)(obj1, obj2)
			index++
		}
		return result
	}
}

/**
 * Extracts a fields from an array of objects into a new array (can also handle objectIDs), null values are ignored
 * @param array Array of objects to extract from
 * @param name Fields name from which to extract the value
 * @param unique True if only unique values should be returned
 * @param asString True if values should be returned as strings (perform .toString() on all values)
 * @returns Array of the extracted values
 */
export function getFieldFromArrayOfObjects<T, O>(array: T[], name: string, unique: boolean, asString: boolean) : O[] {
	if (array == null || array.length == 0) { return [] as O[] }
	const res: O[] = []

	array.forEach(item => {
		const value = getDeepValue<T, unknown>(item, name)
		if (value != null) {
			// Add to list if we do not require unique or if we require unique and it is or if we require unique and its an ObjectID and it is unique
			let want = false
			if (unique) {
				if (!res.includes(value as O)) { want = true }
			} else {
				want = true
			}
			if (want) {
				if (value != null) {
					if (asString) {
						(res as string[]).push(value.toString()) // NOSONAR
					} else {
						res.push(value as O)
					}
				}
			}
		}
	})

	return res
}

/**
 * Set a value in an object, no matter the depth that it is located at
 * @param obj The object to work on
 * @param value The value to apply to the field
 * @param path Path to the field, e.g. main.sub.child
 */
export function setDeepValue<T>(obj: T, value: unknown, path: string) {
	const pathParts = path.split('.')

	if (pathParts.length > 1) {
		const part = pathParts.shift()

		if (obj[part as keyof T] == null || typeof obj[part as keyof T] !== 'object') {
			(obj[part as keyof T] as unknown) = {}
		}
		setDeepValue(obj[part as keyof T], value, path)
	} else {
		(obj[pathParts[0] as keyof T] as unknown) = value
	}
}

/**
 * Get a value from an object, no matter the depth that it is located at
 * @param obj The object to work on
 * @param path Path to the field, e.g. main.sub.child
 * @returns Value of the field
 */
export function getDeepValue<T, O>(obj: T, path: string) : O | null {
	const pathParts = path.split('.')

	if (pathParts.length > 1) {
		const part = pathParts.shift()

		if (obj[part as keyof object] == null || typeof obj[part as keyof object] !== 'object') {
			return null as O | null
		}
		return getDeepValue(obj[part as keyof T], path)
	} else {
		return obj[path as keyof T] as O | null
	}
}

/**
 * Removes a key (or multiple with wildcard) from an object (requires the key to be in the root level).
 * @param obj The object to modify
 * @param key Key to remove - can use 'Price' or '*Price' for e.g. 'YourPrice' or 'price*' for e.g. 'priceWholesale'
 */
export function removeKeyFromObject<T>(obj: T, key: string) {
	let match = null

	if (key.startsWith('*')) {
		key = key.substring(1)
		match = function(value: string) {
			return value.endsWith(key)
		}
	} else if (key.endsWith('*')) {
		key = key.slice(-1)
		match = function(value: string) {
			return value.startsWith(key)
		}
	} else {
		match = function(value: string) {
			return value == key
		}
	}

	Object.keys(obj as object).filter(match).forEach((_key) => { delete obj[_key as keyof T] }) // eslint-disable-line
}

/**
 * Check if an object contains a path
 * @param path Path to check as a string
 * @param obj Object to test
 * @return True if it exists
 */
export function objectPathExists(path: string, obj: object) {
	const parts = path.split('.')
	let cur = obj

	for (const element of parts) {
		if (cur[element as keyof object] != null) {
			cur = cur[element as keyof object]
		} else {
			return false
		}
	}
	return true
}

/**
* Generate a random string
* @param length Length of string
* @returns Random string of given length
*/
export function randomString(length: number) {
	const chars = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ'
	let result = ''

	for (let index = 0; index < length; index++) {
		result += chars.charAt(Math.floor(Math.random() * chars.length))
	}
	return result
}

/**
 * Returns unique values of an array
 * @param array The array to work on
 * @returns Array of distinct values
 */
export function getUnique<T>(array: T[]) {
	return array.filter((value, index, self) => {
		return self.indexOf(value) === index
	})
}

/**
 * Wait for a number of milliseconds
 * @param ms Milliseconds to sleep
 */
export function sleep(ms: number) { return new Promise(resolve => setTimeout(resolve, ms)) }

/**
 * Transform a string query filter to a regular expression that will match each word in the string
 * @param filter String filter
 * @return Regular expression
 */
export function getRegExFromFilter(filter: string) {
	filter = '^' + filter.trim()
		.toLowerCase()
		.replace(/ {2}/g, ' ')
		.replace(/\./g, '\\.')
		.replace(/[[\]|(),$^{}\t]/g, '')
		.split(' ')
		.map((item) => { return '(?=.*?' + item + ')' })
		.join('') + '.*$'

	return new RegExp(filter, 'i')
}

/**
 * Check if a number is a phone number, if no + we assume +45, spaces and dashes are ignored (they are okay)
 * @param number Number to validate
 * @param empty Is string considered valid if empty - optional
 * @returns True if valid
 */
export function isPhoneNumber(number?: string, empty = false) {
	if (empty && (number == null || number.length === 0)) { return true } // empty string is valid if empty flag is set

	if (!number) { return false }

	number = number.replace(/[ -]/g, '')
	if (number.startsWith('00')) { number = '+' + number.substring(1) }
	if (!number.startsWith('+')) { number = '+45' + number }
	return isE164(number, true)
}

/**
 * Evaluates if a number is formatted according to E.164 (+CCABCDEFG...)
 * @param number The number to check
 * @param strict True if only regular numbers are allowed
 * @returns True if valid
 */
export function isE164(number?: string, strict = false) {
	if (!number) { return false }

	if (number.startsWith('+45')) {
		// DK
		if (RegExp(/^\+45[2-9]\d{7}$/).exec(number) != null) { return true }
		if (RegExp(/^\+45(112|114|118|18\d\d|16\d\d\d)$/).exec(number) != null) { return !strict }
		return false
	} else if (number.startsWith('+46')) {
		// SE
		return RegExp(/^\+46\d{7,9}$/).exec(number) != null
	} else if (number.startsWith('+47')) {
		// NO
		if (RegExp(/^\+470[2-9]\d{3}$/).exec(number) != null) { return true }
		return RegExp(/^\+47\d{8}$/).exec(number) != null
	} else {
		return RegExp(/^\+\d{7,}$/).exec(number) != null // Rest of world
	}
}

/**
* Return the difference between 2 dates
* @param date1 From date
* @param date2 To date
* @param interval Interval to return: YEARS, MONTHS, WEEKS, DAYS, HOURS, MINUTES, SECONDS
* @return Interval, returns NaN or undefined if it fails
*/
export function dateDiff(date1: Date, date2: Date, interval: 'YEARS' | 'MONTHS' | 'WEEKS' | 'DAYS' | 'HOURS' | 'MINUTES' | 'SECONDS') {
	const second = 1000
	const minute = second * 60
	const hour = minute * 60
	const day = hour * 24
	const week = day * 7

	date1 = new Date(date1)
	date2 = new Date(date2)

	const timediff = date2.getTime() - date1.getTime()

	switch (interval) {
		case 'YEARS': return date2.getFullYear() - date1.getFullYear()
		case 'MONTHS': return (((date2.getFullYear() * 12) + date2.getMonth()) - ((date1.getFullYear() * 12) + date1.getMonth()))
		case 'WEEKS': return Math.round(timediff / week)
		case 'DAYS': return Math.round(timediff / day)
		case 'HOURS': return Math.round(timediff / hour)
		case 'MINUTES': return Math.round(timediff / minute)
		case 'SECONDS': return Math.round(timediff / second)
		default: return undefined
	}
}

export function openMailClient(emailAddress: string, subject?: string, body?: string) {
	const url = `mailto:${encodeURIComponent(emailAddress)}${subject ? `?subject=${encodeURIComponent(subject)}${body ? `&body=${encodeURIComponent(body)}` : ''}` : ''}` // NOSONAR

	if (import.meta.env.DEV) {
		// Use anchor click to avoid debugger pause in dev
		const a = document.createElement('a')

		a.href = url
		a.style.display = 'none'
		document.body.appendChild(a)
		a.click()
		document.body.removeChild(a)
	} else {
		// Use normal open in production
		window.open(url)
	}
}
